import React, { useContext, useEffect, useState } from "react";
import { LanguageContext } from '../components/LanguageContext';

const OurCenters = () => {
  const { language } = useContext(LanguageContext);
  const [centers, setCenters] = useState([]);
  const [groupedCenters, setGroupedCenters] = useState({});
  const [expandedDistricts, setExpandedDistricts] = useState({});

  useEffect(() => {
    fetch('/api/get/centers', {
      headers: {
        'X-CSRFToken': window.CSRF_TOKEN || '',
      },
      skipInterceptor: true,
    })
      .then(response => response.json())
      .then(data => {
        setCenters(data);
        
        // Group centers by district
        const grouped = data.reduce((acc, center) => {
          if (!acc[center.district]) {
            acc[center.district] = [];
          }
          acc[center.district].push(center);
          return acc;
        }, {});
        
        setGroupedCenters(grouped);
        
        // Initialize all districts as expanded
        const initialExpandState = Object.keys(grouped).reduce((acc, district) => {
          acc[district] = true;
          return acc;
        }, {});
        
        setExpandedDistricts(initialExpandState);
      })
      .catch(error => console.error('Error fetching centers:', error));
  }, []);
  
  const toggleDistrict = (district) => {
    setExpandedDistricts(prev => ({
      ...prev,
      [district]: !prev[district]
    }));
  };

  return (
    <>
      {/* Header Section */}
      <div className="relative">
        <div className="h-[300px] relative">
          <img 
            src="https://th-i.thgim.com/public/migration_catalog/article10456065.ece/alternates/FREE_1200/amaravathi"
            alt={language === 'en' ? "Our Centers Header" : "ہمارے مراکز ہیڈر"} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/40">
            <div className="container mx-auto px-8 h-full flex items-center">
              <div className="max-w-2xl text-white">
                <h1 className="text-3xl md:text-5xl font-bold mb-4">
                  {language === 'en' ? "Our Centers" : "ہمارے مراکز"}
                </h1>
                <p className="md:text-lg md:text-xl">
                  {language === 'en' ? "Discover our network of Urdu learning facilities across Andhra Pradesh" : "آندھرا پردیش میں ہمارے اردو سیکھنے کے مراکز کی دریافت کریں"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Centers Section */}
      <div className="container mx-auto px-8 py-12">
        {Object.keys(groupedCenters).length === 0 ? (
          <section>
            <div className="container mx-auto px-8">
              <h2 className="text-3xl font-bold text-blue-800 mb-6">
                {language === 'en' ? "Centers Coming Soon" : "مراکز جلد آرہے ہیں"}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-2">
                    {language === 'en' ? "New Centers" : "نئے مراکز"}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'en' ? "We are working on opening new centers in more districts. Stay tuned!" : "ہم مزید اضلاع میں نئے مراکز کھولنے پر کام کر رہے ہیں۔ ہمارے ساتھ جڑے رہیں!"}
                  </p>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <div className="space-y-12">
            {Object.entries(groupedCenters).map(([district, districtCenters]) => (
              <div key={district} className="border-b pb-8 last:border-b-0">
                <div 
                  className="flex items-center justify-between cursor-pointer mb-6"
                  onClick={() => toggleDistrict(district)}
                >
                  <h2 className="text-3xl font-bold text-blue-800">
                    {district} {language === 'en' ? "District" : "ضلع"}
                    <span className="text-sm ml-2 text-blue-600">
                      ({districtCenters.length} {language === 'en' ? "centers" : "مراکز"})
                    </span>
                  </h2>
                  <span className="text-2xl text-blue-800">
                    {expandedDistricts[district] ? '−' : '+'}
                  </span>
                </div>
                
                {expandedDistricts[district] && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {districtCenters.map((center) => (
                      <div key={center.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                        {center.map_url && (
                          <iframe 
                            src={center.map_url}
                            title={`${center.name} Map`}
                            className="w-full h-80"
                            frameBorder="0"
                            style={{ border: 0 }}
                            allowFullScreen
                          ></iframe>
                        )}

                        <div className="p-6">
                          <h3 className="text-xl font-bold text-blue-800 mb-2">{center.name}</h3>
                          <p className="text-gray-600 mb-2">
                            <span className="font-semibold">{language === 'en' ? "Address" : "پتہ"}:</span> {center.address}
                          </p>

                          {/* Employee List */}
                          {center.employees.length > 0 && (
                            <>
                              <h4 className="text-lg font-semibold text-gray-700 mb-2">{language === 'en' ? "Employees" : "عملہ"}</h4>
                              <ul className="text-gray-600">
                                {center.employees.map((employee) => (
                                  <li key={employee.id} className="mb-2">
                                    <span className="font-semibold">{employee.name}</span> - {employee.designation}
                                    {employee.contact_number && (
                                      <> ({language === 'en' ? "Contact" : "رابطہ"}: {employee.contact_number})</>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default OurCenters;